import React from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { BookOpen } from "lucide-react";

const Section = ({ n, title, children }) => (
  <div className="mb-5" data-testid={`guide-section-${n}`}>
    <h3 className="text-sm font-semibold text-slate-900 mb-1.5">{n}. {title}</h3>
    <div className="text-sm text-slate-600 space-y-1.5 leading-relaxed">{children}</div>
  </div>
);

export const FleetGuideSheet = ({ open, onOpenChange }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="fleet-guide-sheet">
      <div className="flex items-center gap-2 mb-1 pt-1">
        <BookOpen className="w-5 h-5 text-gbe" />
        <h2 className="text-lg font-semibold text-slate-900">Fleet &amp; Vehicles — Quick Guide</h2>
      </div>
      <p className="text-xs text-slate-400 mb-5">A practical walkthrough for the logistics &amp; procurement manager.</p>

      <Section n={1} title="The Fleet list">
        <p>One row per vehicle — rego, vehicle, type, driver, base, odometer, last service and status. Search by rego/make/model/driver/base, filter by status and type, and click a row for the full scorecard.</p>
      </Section>

      <Section n={2} title="Assign a driver (do this first — it matters most)">
        <p>AroFlo files every service job under the <b>driver's name</b> — there is no rego on the job. An accurate driver is what links jobs and costs to a vehicle.</p>
        <p>Click the <b>pencil</b> next to a driver in the list → type the name → press Enter. Or use the scorecard's <b>Edit</b>. Your changes survive the nightly AroFlo sync. Clear the field to revert to the AroFlo value.</p>
      </Section>

      <Section n={3} title="Vehicle scorecard">
        <p>Service status (Overdue / Due soon / OK), last service (latest completed job or the asset record), the full <b>service &amp; repair history</b> with cost, maintenance spend, and condition/risk/replacement signals. <i>"not costed yet"</i> means AroFlo hasn't invoiced that job yet.</p>
      </Section>

      <Section n={4} title="Service alerts">
        <p>The <b>Service alerts</b> button sets the service interval (months / km) and the due-soon window, and manages who receives the email digests. These drive the Overdue / Due-soon flags.</p>
      </Section>

      <Section n={5} title="Odometer from Ampol">
        <p>Odometers update automatically from the monthly Ampol fuel report — the Hub reads the km keyed at the pump and always uses the latest reading per vehicle.</p>
        <p>Drivers who don't key the odometer create <b>0-km fills</b> that can't update the vehicle. Use the <b>Ampol missing-km report</b> (Export menu) to chase them.</p>
      </Section>

      <Section n={6} title="Sync job costs">
        <p>Pulls job cost from AroFlo (material + labour) for any job missing a cost. Runs automatically every night; press the button for an immediate refresh — it runs in the background and the list updates when done.</p>
      </Section>

      <Section n={7} title="Exports (the Export menu)">
        <p><b>Vehicle asset register</b> — the full fleet. <b>Service due list</b> — every overdue/due-soon vehicle for the workshop. <b>Ampol missing-km report</b> — fuel fills with no odometer. <b>Replacement shortlist</b> — the end-of-life ranking with estimated 12-month spend. <b>Unmatched service jobs</b> — jobs that couldn't be tied to a vehicle.</p>
      </Section>

      <Section n={8} title="Your routine">
        <p><b>Weekly:</b> check the Overdue / Due-soon tiles, export the Service due list for the workshop, and fix any driver assignments flagged in the amber banner.</p>
        <p><b>Monthly (after Ampol lands):</b> chase the Ampol missing-km offenders (an automatic email also goes out on the 10th), and press Sync job costs before reporting.</p>
        <p><b>As needed:</b> keep drivers current when a vehicle changes hands, and use the Replacement shortlist for procurement reviews.</p>
      </Section>

      <p className="text-xs text-slate-400 border-t border-slate-100 pt-3 mt-2">
        Tip: if the workshop names AroFlo jobs with the rego (e.g. <span className="font-mono">BA19VX - Vehicle Service/Repair</span>), the Hub auto-matches them — far more jobs will tie to a vehicle.
      </p>
    </SheetContent>
  </Sheet>
);
